/**
 * `<center-well>`: the stock on the left and the discard pile on the right,
 * from the player's side of the table.
 *
 * The discard pile is one target with two turns' worth of meaning: what you
 * take in the draw phase, and what you throw to in the play phase. The phases
 * never overlap, so which one a click means is never in doubt.
 */

import type { Card } from "@ryzun/haf-saloon-game-engine";
import { getCardLabel, getCardPhrase } from "../cards.js";
import { h, setAttribute } from "../dom.js";
import { emit } from "../events.js";
import { BaseElement } from "./base-element.js";
import styles from "./center-well.css";
import "./card-pile.js";

export interface WellModel {
    readonly stockCount: number;
    readonly discardTop: Card | null;
    readonly discardCount: number;
    readonly discardFrozen: boolean;
    readonly canDrawStock: boolean;
    readonly canTakeDiscard: boolean;
    /** The card a click would throw, in the play phase; null when none would. */
    readonly discardCard: Card | null;
    /** Why the pile cannot be taken, shown under the piles when it cannot. */
    readonly hint: string | null;
    /** True while a finished round is counted out; the stock steps aside for the count's caller. */
    readonly counting: boolean;
}

function describeDiscardPile(model: WellModel): string {
    if (model.discardTop === null) {
        return "Discard pile, empty";
    }

    const frozen = model.discardFrozen ? ", frozen" : "";

    return `Discard pile, ${model.discardCount} cards, top card ${getCardLabel(model.discardTop)}${frozen}`;
}

export class CenterWell extends BaseElement {
    #model: WellModel | null = null;

    readonly #stock = document.createElement("playing-card-pile");

    readonly #discard = document.createElement("playing-card-pile");

    readonly #stockButton = h("button", { "type": "button", "class": "target", "data-chip": "Draw 2" }, this.#stock);

    readonly #discardButton = h("button", { type: "button", class: "target" }, this.#discard);

    readonly #dropNote = h("p", { "class": "drop-note", "aria-hidden": "true" });

    readonly #hint = h("p", { class: "hint" });

    constructor() {
        super(styles);

        this.#stock.type = "stock";
        this.#discard.type = "discard";

        this.#stockButton.addEventListener("click", () => emit(this, "draw-stock", null));
        this.#discardButton.addEventListener("click", () => {
            emit(this, this.#model?.discardCard === null ? "take-discard" : "discard", null);
        });

        this.root.append(
            h("div", { class: "piles" }, this.#stockButton, this.#discardButton),
            h("div", { class: "notes" }, this.#dropNote, this.#hint)
        );
    }

    /** Where a stock draw leaves from. */
    get stockBox(): DOMRect {
        return this.#stock.getBoundingClientRect();
    }

    /** Where every card is counted onto at the end of a round. */
    get discardBox(): DOMRect {
        return this.#discard.getBoundingClientRect();
    }

    set model(value: WellModel | null) {
        this.#model = value;
        this.invalidate();
    }

    protected render(): void {
        const model = this.#model;

        if (model === null) {
            return;
        }

        this.#stock.count = model.stockCount;
        this.#stockButton.disabled = !model.canDrawStock;
        this.#stockButton.classList.toggle("set-aside", model.counting);
        setAttribute(this.#stockButton, "aria-label", `Draw two from the stock, ${model.stockCount} cards remaining`);

        this.#discard.count = model.discardCount;
        this.#discard.top = model.discardTop;
        this.#discard.frozen = model.discardFrozen;
        this.#discardButton.disabled = !model.canTakeDiscard && model.discardCard === null;
        setAttribute(this.#discardButton, "data-chip", model.discardCard === null ? "Take pile" : "Ends turn");
        setAttribute(this.#discardButton, "aria-label", model.discardCard === null
            ? `Take the pile with the selected cards. ${describeDiscardPile(model)}`
            : `Discard the ${getCardLabel(model.discardCard)} and end your turn. ${describeDiscardPile(model)}`);

        // The pile replaced the discard button, so it has to say out loud that
        // it is the one that took its place. The button's own label says it to
        // a screen reader, so this copy is hidden from one.
        this.#dropNote.hidden = model.discardCard === null;
        this.#dropNote.textContent = model.discardCard === null
            ? ""
            : `Click the pile to discard the ${getCardPhrase(model.discardCard)} and end your turn`;

        this.#hint.hidden = model.hint === null;
        this.#hint.textContent = model.hint ?? "";
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "center-well": CenterWell;
    }
}

customElements.define("center-well", CenterWell);
